import { default as React, FunctionComponent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useTitle from 'react-use/lib/useTitle'

import useThunkDispatch from '../common/useThunkDispatch'
import Splash from '../components/Splash'
import ToggleButtonGroup from '../components/ToggleButtonGroup'
import {
  ISpecies,
  requestSpecies,
  setSpeciesGroup,
  SpeciesGroup,
} from './speciesActions'
import { speciesSelector } from './speciesSelectors'

type Filter = 'All' | 'Pest' | 'Animal' | 'Unclassified'

const FILTERS: Filter[] = ['All', 'Pest', 'Animal', 'Unclassified']
const GROUPS: SpeciesGroup[] = ['pest', 'animal']
const GROUP_LABELS: Record<SpeciesGroup, string> = {
  pest: 'Pest',
  animal: 'Animal',
}

const matchesFilter = (species: ISpecies, filter: Filter): boolean => {
  if (filter === 'All') return true
  if (filter === 'Unclassified') return species.group == null
  return species.group === filter.toLowerCase()
}

const productsTotal = (species: ISpecies): number =>
  species.productsForshaw + species.productsFrs

const Species: FunctionComponent = () => {
  useTitle('Product Attributes - Forshaw')

  const dispatch = useThunkDispatch()
  const species: ISpecies[] = useSelector(speciesSelector)

  const [filter, setFilter] = useState<Filter>('All')
  const [loading, setLoading] = useState(true)
  // Display order is frozen on first load so classifying a row doesn't make it
  // jump/disappear on click; only the group button state changes in place.
  const [orderedIds, setOrderedIds] = useState<number[]>([])

  useEffect(() => {
    setLoading(true)
    dispatch(requestSpecies()).finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (species.length === 0 || orderedIds.length > 0) return

    const ids = [...species]
      .sort((a, b) => {
        // Unclassified first, then by product count descending
        const aUnclassified = a.group == null
        const bUnclassified = b.group == null
        if (aUnclassified !== bUnclassified) return aUnclassified ? -1 : 1
        return productsTotal(b) - productsTotal(a)
      })
      .map((s) => s.id)

    setOrderedIds(ids)
  }, [species, orderedIds.length])

  const counts: Record<Filter, number> = {
    All: species.length,
    Pest: species.filter((s) => s.group === 'pest').length,
    Animal: species.filter((s) => s.group === 'animal').length,
    Unclassified: species.filter((s) => s.group == null).length,
  }

  const speciesById = new Map(species.map((s) => [s.id, s]))

  const rows = orderedIds
    .map((id) => speciesById.get(id))
    .filter((s): s is ISpecies => s != null)
    .filter((s) => matchesFilter(s, filter))

  return (
    <div className="flexible-container table-page-layout">
      <div className="filter">
        <div className="filter_col">
          <ToggleButtonGroup<Filter>
            options={FILTERS}
            value={filter}
            renderOption={(f) => `${f} (${counts[f]})`}
            onChange={setFilter}
          />
        </div>
      </div>

      <div className="table">
        <div className="table_head-tr">
          <div className="table_td -g16 table_title">Sana ID</div>
          <div className="table_td -g33 table_title">Attribute</div>
          <div className="table_td -g25 table_title">
            Products (Forshaw / FRS)
          </div>
          <div className="table_td -g25 table_title">Group</div>
        </div>
      </div>

      <div className="table_loading-wrapper">
        <div className="wonder-scroll" style={{ height: '100%' }}>
          <div className="table">
            {rows.length === 0 && !loading && (
              <div className="table_head-tr">
                <div className="table_head-tr table_td table_title">
                  No species found
                </div>
              </div>
            )}

            {rows.map((s) => (
              <div className="table_tr" key={s.id}>
                <div className="table_td -g16">{s.sanaId}</div>
                <div className="table_td -g33">{s.name}</div>
                <div className="table_td -g25">
                  {s.productsForshaw} / {s.productsFrs}
                </div>
                <div className="table_td -g25">
                  <ToggleButtonGroup<SpeciesGroup | null>
                    className="-compact"
                    options={GROUPS}
                    value={s.group}
                    renderOption={(g) => GROUP_LABELS[g as SpeciesGroup]}
                    onChange={(g) => g && dispatch(setSpeciesGroup(s.id, g))}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <Splash isLoading={loading} bgColor="var(--black-squeeze)" />
      </div>
    </div>
  )
}

export default Species
